import { useCallback, useRef } from "react";
import { throttleFn } from "@tellescope/utils";
import { useTellescopeMutation } from "@tellescope/tanstack-query";
import { useEnduserStore } from "../store/hooks";
import type { CustomFields } from "@tellescope/types-models";

export const useTrackEngagementEvent = () => {
  const session = useEnduserStore((state) => state.session);
  const mutation = useTellescopeMutation({
    model: "engagement_events",
    method: "createOne",
    session: session ?? undefined,
    autoCacheUpdateStrategy: "disabled",
  });

  // One throttled sender per event type, keyed by type string
  const throttlesRef = useRef(new Map<string, (type: string, significance: number, fields?: CustomFields) => void>());

  const track = useCallback(
    (type: string, significance: number, fields?: CustomFields) => {
      // Read from store at call time so this works immediately after login
      // before the component re-renders with updated session/enduserId
      const { session: currentSession, currentEnduser } = useEnduserStore.getState();
      if (!currentSession || !currentEnduser) return;

      if (!throttlesRef.current.has(type)) {
        throttlesRef.current.set(
          type,
          throttleFn((t: string, s: number, f?: CustomFields) => {
            const enduserId = useEnduserStore.getState().currentEnduser?.id;
            if (!enduserId) return;
            mutation.mutate({ enduserId, type: t, significance: s, timestamp: new Date(), fields: f });
          }, 3000)
        );
      }

      throttlesRef.current.get(type)!(type, significance, fields);
    },
    [mutation]
  );

  return { track };
};
